/**
 * PATH: packages/ui/src/kanban/cloneBoardLanes.ts
 * FILE_ID: UI-KANBAN-UTIL-002
 * PURPOSE: Clear the auto-provisioned default lanes off a freshly-created
 *          board so a "clone from existing board" flow can mirror the
 *          template's lanes without colliding with the defaults.
 * WHY:
 *   BUG-2026-07-03-KANBAN-CLONE-LANES — creating a board with "clone from
 *   existing" showed the DEFAULT lanes, not the template's. Root cause:
 *   `BoardViewSet.perform_create` unconditionally provisions the kind's
 *   default lanes, so by the time the FE clone loop runs `createLane` for
 *   each template lane, any whose name matches a default (e.g. "To Do")
 *   trips the `(board, name)` partial-unique constraint. The FE swallowed
 *   those errors, so the board silently kept its defaults.
 *
 *   Fix (FE-only): before mirroring the template's lanes, soft-delete the
 *   provisioned defaults. `DELETE /lanes/{id}/` stamps `deleted_at`, and the
 *   unique constraint is scoped to `deleted_at IS NULL`, so the names are
 *   freed for the template lanes. Safe because this only runs immediately
 *   after createBoard — the board has no cards yet.
 * FLOW:
 *   ┌──────────────────┐   ┌───────────────────────┐   ┌────────────────────┐
 *   │ new board id     │ → │ listLanes → deleteLane │ → │ board with no lanes │
 *   │ (defaults only)  │   │   (each default)       │   │ (ready for clone)   │
 *   └──────────────────┘   └───────────────────────┘   └────────────────────┘
 * DEPENDENCIES:
 *   - ./api (KanbanApi): listLanes + deleteLane
 * RELATED:
 *   - ./duplicateBoard.ts (clone path)
 *   - ./components/board-list/useBoardListMutations.ts (clone path)
 *   - ./components/board-gallery/createBoardFlow.ts (clone path)
 *   - ../../kanban-host/src/allocation/useCreateBoardFromPicker.ts (clone path)
 */

import type { KanbanApi } from './api'

/**
 * Soft-delete every currently-active lane on `boardId`.
 *
 * Intended to run ONLY on a brand-new board (right after `createBoard`)
 * to strip the backend's auto-provisioned default lanes before a clone
 * mirrors the template's lanes. `listLanes` returns only live
 * (`deleted_at IS NULL`, non-archived) lanes, so this clears exactly the
 * set that would otherwise collide on the `(board, name)` unique
 * constraint.
 *
 * Best-effort: a single failed `deleteLane` is swallowed so a partial
 * cleanup still lets the clone proceed (the operator can tidy leftovers
 * manually). A failed `listLanes` propagates to the caller's existing
 * clone try/catch, which already treats clone failures as non-fatal.
 */
export async function clearProvisionedLanes(
  api: KanbanApi,
  boardId: string,
): Promise<void> {
  // `deleteLane` is an optional capability on the KanbanApi contract — a
  // tenant that doesn't wire it can't be cleared, so bail (the clone then
  // falls back to its prior behaviour). Capture it in a local so the null
  // check narrows across the awaited loop below.
  const deleteLane = api.deleteLane
  if (!deleteLane) return
  const existing = await api.listLanes(boardId)
  for (const lane of existing) {
    try {
      await deleteLane(lane.id)
    } catch {
      // Best-effort — leave this lane in place; the clone can still add
      // its own lanes, and the operator can archive stragglers manually.
    }
  }
}
